// src/components/schedule-form.js - Reusable Schedule Form Modal Component with reCAPTCHA v3 and i18n support
import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslation } from "gatsby-plugin-react-i18next"

const ScheduleForm = ({ 
  isOpen, 
  onClose, 
  accountId, 
  agencyId, 
  familyName 
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    message: ''
  })
  
  const [scheduleErrors, setScheduleErrors] = useState({})
  const [isScheduleSubmitting, setIsScheduleSubmitting] = useState(false)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', null
  
  const isClient = typeof window !== 'undefined'
  const recaptchaSiteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY || process.env.RECAPTCHA_SITE_KEY

  // Compact reCAPTCHA loader
  const loadRecaptchaScript = useCallback(async () => {
    if (!recaptchaSiteKey || !isClient) return false
    
    if (window.grecaptcha?.ready) {
      return new Promise(resolve => window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true)
        resolve(true)
      }))
    }

    const existing = document.querySelector(`script[src*="recaptcha/api.js"]`)
    if (existing) {
      return new Promise(resolve => {
        const check = () => window.grecaptcha?.ready ? 
          window.grecaptcha.ready(() => { setRecaptchaLoaded(true); resolve(true) }) : 
          setTimeout(() => resolve(!!window.grecaptcha), 500)
        
        existing.readyState === 'complete' ? check() : 
        existing.addEventListener('load', check, { once: true })
      })
    }

    return new Promise(resolve => {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`
      script.async = script.defer = true
      script.onload = () => window.grecaptcha?.ready ? 
        window.grecaptcha.ready(() => { setRecaptchaLoaded(true); resolve(true) }) : resolve(false)
      script.onerror = () => resolve(false)
      document.head.appendChild(script)
    })
  }, [recaptchaSiteKey])

  // Compact reCAPTCHA executor
  const executeRecaptcha = useCallback(async (action = 'schedule_form') => {
    try {
      if (!recaptchaLoaded && !(await loadRecaptchaScript())) return null
      return window.grecaptcha?.execute(recaptchaSiteKey, { action }) || null
    } catch { return null }
  }, [recaptchaLoaded, recaptchaSiteKey, loadRecaptchaScript])

  // Validation functions
  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }, [])

  const validateUSPhoneNumber = useCallback((phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '')
    // US phone numbers should be exactly 10 digits
    return cleanPhone.length === 10
  }, [])

  const formatPhoneNumber = useCallback((phone) => {
    const cleanPhone = phone.replace(/\D/g, '')
    if (cleanPhone.length === 10) {
      return cleanPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    }
    return phone
  }, [])

  const formatPhoneNumberAsTyping = useCallback((phone) => {
    const cleanPhone = phone.replace(/\D/g, '').slice(0, 10) // Limit to 10 digits
    
    if (cleanPhone.length === 0) return ''
    if (cleanPhone.length <= 3) return cleanPhone
    if (cleanPhone.length <= 6) return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3)}`
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`
  }, [])

  // Schedule form validation
  const validateScheduleForm = useCallback(() => {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = t('scheduleModal.validation.nameRequired')
    }

    if (!formData.email.trim()) {
      errors.email = t('scheduleModal.validation.emailRequired')
    } else if (!validateEmail(formData.email)) {
      errors.email = t('scheduleModal.validation.emailInvalid')
    }

    if (!formData.phone.trim()) {
      errors.phone = t('scheduleModal.validation.phoneRequired')
    } else if (!validateUSPhoneNumber(formData.phone)) {
      errors.phone = t('scheduleModal.validation.phoneInvalid')
    }

    if (!formData.preferred_date) {
      errors.preferred_date = t('scheduleModal.validation.dateRequired')
    } else {
      const selectedDate = new Date(formData.preferred_date)
      const now = new Date()
      if (selectedDate <= now) {
        errors.preferred_date = t('scheduleModal.validation.dateInFuture')
      }
    }

    return errors
  }, [formData, validateEmail, validateUSPhoneNumber, t])

  // API submission function
  const submitToAPI = useCallback(async (payload) => {
    const response = await fetch(`${process.env.GATSBY_PF_API_URL || process.env.PF_API_URL}DomainForm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        account: accountId,
        agency: agencyId,
        family: familyName,
        domain: typeof window !== 'undefined' ? window.location.href : ''
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    return await response.json()
  }, [accountId, agencyId, familyName])

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferred_date: '',
        message: ''
      })
      setScheduleErrors({})
      setSubmitStatus(null)
    }
  }, [isOpen])

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target
    
    let formattedValue = value
    
    // Format phone number as user types
    if (name === 'phone') {
      formattedValue = formatPhoneNumberAsTyping(value)
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }))
    
    // Clear error when user starts typing
    if (scheduleErrors[name]) {
      setScheduleErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Clear submit status when user makes changes
    if (submitStatus) {
      setSubmitStatus(null)
    }
  }, [scheduleErrors, formatPhoneNumberAsTyping, submitStatus])

  // Scroll to top of modal
  const scrollModalToTop = useCallback(() => {
    if (!isClient) return
    const modal = document.querySelector('.modal-content')
    if (modal) {
      modal.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    const errors = validateScheduleForm()
    if (Object.keys(errors).length > 0) {
      setScheduleErrors(errors)
      setSubmitStatus('error')
      // Scroll to top of modal to show errors
      setTimeout(scrollModalToTop, 100)
      return
    }

    setIsScheduleSubmitting(true)
    setSubmitStatus(null)
    
    try {
      let recaptchaToken = null
      
      // Get reCAPTCHA token
      if (recaptchaSiteKey) {
        recaptchaToken = await executeRecaptcha('schedule_form_submit')
      }

      const payload = {
        type: 'Schedule',
        name: familyName,
        ...formData,
        phone: formatPhoneNumber(formData.phone),
        ...(recaptchaToken && { recaptcha_token: recaptchaToken })
      }

      await submitToAPI(payload)
      
      setSubmitStatus('success')
      
      // Scroll to top of modal to show success message
      setTimeout(scrollModalToTop, 100)
      
      // Close modal after successful submission with delay to show success message
      setTimeout(() => {
        onClose()
      }, 5000)
      
    } catch (error) {
      console.error('Error submitting schedule form:', error)
      setSubmitStatus('error')
      
      // Check if error is reCAPTCHA related
      if (error.message.includes('reCAPTCHA')) {
        setScheduleErrors({ 
          submit: t('financialModal.securityError')
        })
      } else {
        setScheduleErrors({ 
          submit: t('scheduleModal.errorMessage')
        })
      }
      
      // Scroll to top of modal to show error message
      setTimeout(scrollModalToTop, 100)
    } finally {
      setIsScheduleSubmitting(false)
    }
  }, [validateScheduleForm, executeRecaptcha, recaptchaLoaded, recaptchaSiteKey, formData, formatPhoneNumber, submitToAPI, familyName, onClose, scrollModalToTop, t])

  // Success/Error messages
  const statusMessage = useMemo(() => {
    if (submitStatus === 'success') {
      return (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg font-body" role="alert">
          <div className="flex items-center">
            <span className="mr-2">✅</span>
            <span className="font-semibold">{t('scheduleModal.successMessage')}</span>
          </div>
        </div>
      )
    }
    
    if (submitStatus === 'error' && scheduleErrors.submit) {
      return (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg font-body" role="alert">
          <div className="flex items-center">
            <span className="mr-2">⚠️</span>
            <span className="font-semibold">{t('common.error')}</span>
          </div>
          <p className="mt-1 text-sm">{scheduleErrors.submit}</p>
        </div>
      )
    }
    
    return null
  }, [submitStatus, scheduleErrors.submit, t])

  // Show warning if reCAPTCHA is not configured
  const recaptchaWarning = useMemo(() => {
    if (!recaptchaSiteKey) {
      return (
        <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg font-body" role="alert">
          <div className="flex items-center">
            <span className="mr-2">⚠️</span>
            <span className="font-semibold">{t('contact.warning')}</span>
          </div>
          <p className="mt-1 text-sm">{t('financialModal.recaptchaWarning')}</p>
        </div>
      )
    }
    return null
  }, [recaptchaSiteKey, t])

  // Error message component
  const ErrorMessage = useCallback(({ error }) => {
    if (!error) return null
    return <p className="text-red-500 text-sm mt-1 font-body">{error}</p>
  }, [])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-70 transition-opacity cursor-pointer"
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto modal-content">
        <div className="flex items-center justify-center p-6 border-b border-gray-200 relative">
          <h2 className="text-2xl font-semibold text-center font-heading-main">{t('scheduleModal.title')}</h2>
          <button
            onClick={onClose}
            className="absolute right-6 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            disabled={isScheduleSubmitting}
            aria-label={t('common.close')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* reCAPTCHA Warning */}
          {recaptchaWarning}

          {/* Status Messages */}
          {statusMessage}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
              {t('scheduleModal.fields.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors cursor-text font-body ${
                scheduleErrors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isScheduleSubmitting}
            />
            <ErrorMessage error={scheduleErrors.name} />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
              {t('scheduleModal.fields.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors cursor-text font-body ${
                scheduleErrors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isScheduleSubmitting}
            />
            <ErrorMessage error={scheduleErrors.email} />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
              {t('scheduleModal.fields.phone')} *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('financialModal.placeholders.phone')}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors cursor-text font-body ${
                scheduleErrors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isScheduleSubmitting}
            />
            <ErrorMessage error={scheduleErrors.phone} />
          </div>

          <div>
            <label htmlFor="preferred_date" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
              {t('scheduleModal.fields.dateTime')} *
            </label>
            <div className="relative">
              <input
                type="datetime-local"
                id="preferred_date"
                name="preferred_date"
                value={formData.preferred_date}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors cursor-pointer font-body ${
                  scheduleErrors.preferred_date ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isScheduleSubmitting}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <ErrorMessage error={scheduleErrors.preferred_date} />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
              {t('scheduleModal.fields.message')}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent transition-colors resize-none cursor-text font-body"
              placeholder={t('scheduleModal.placeholders.message')}
              disabled={isScheduleSubmitting}
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-primary-dark flex-1 px-6 py-3 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 cursor-pointer font-heading-sub"
              disabled={isScheduleSubmitting}
            >
              {t('financialModal.buttons.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 px-6 py-3 font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center cursor-pointer font-heading-sub"
              disabled={isScheduleSubmitting}
            >
              {isScheduleSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('financialModal.buttons.submitting')}
                </>
              ) : (
                t('financialModal.buttons.submit')
              )}
            </button>
          </div>

          {/* reCAPTCHA Badge Note */}
          {recaptchaSiteKey && (
            <div className="text-center pt-4">
              <p className="text-xs text-gray-500 font-body">
                {t('financialModal.recaptchaNotice')}
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        
        .focus\\:ring-primary-green:focus {
          --tw-ring-color: #9bd45a;
        }
      `}</style>
    </div>
  )
}

export default ScheduleForm